import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  DisplayProps,
  FlexDirectionProps,
  FlexWrapProps,
  JustifyContentProps,
  OverflowProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  borderBottom,
  display,
  flexDirection,
  flexWrap,
  justifyContent,
  overflow,
  space,
} from 'styled-system';

import ClearAllFiltersButton from 'common/components/ClearAllFiltersButton';
import { Flex } from 'common/components/Flex';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import type {
  BorderBottomColorProps,
  BorderBottomStyleProps,
  BorderBottomWidthProps,
  ThemeHeightProps,
} from 'common/theme/system-utilities';
import {
  borderBottomColor,
  borderBottomStyle,
  borderBottomWidth,
  themeHeight,
} from 'common/theme/system-utilities';
import type { Filter, FiltersParams } from 'common/types/SortAndFilter';
import FilterChip from './FilterChip';
import { getAllSelectedFilters } from './utils';

const SelectedFiltersWrapper = styled.section<
  DisplayProps &
    FlexDirectionProps &
    OverflowProps &
    SpaceProps &
    AlignItemsProps &
    BackgroundColorProps
>`
  ${display};
  ${space};
  ${flexDirection};
  ${overflow};
  ${alignItems};
  ${backgroundColor};
  scrollbar-width: none;
`;

const selectedFiltersWrapperStyles: ComponentProps<typeof SelectedFiltersWrapper> = {
  display: 'flex',
  flexDirection: 'row',
  overflow: 'auto',
  alignItems: 'center',
  mt: 'sp_0',
  pl: ['sp_16', 'sp_24', 'sp_0'],
  backgroundColor: 'sys.neutral.background.hover',
};

const ClearAllFiltersContainer = styled.div<
  DisplayProps &
    AlignItemsProps &
    FlexWrapProps &
    JustifyContentProps &
    ThemeHeightProps &
    BorderBottomStyleProps &
    BorderBottomWidthProps &
    BorderBottomColorProps &
    SpaceProps
>`
  ${display};
  ${alignItems};
  ${flexWrap};
  ${justifyContent};
  ${themeHeight};
  ${borderBottomStyle};
  ${borderBottomWidth};
  ${borderBottomColor};
  ${borderBottom};
  ${space};
`;

const clearAllFiltersContainerStyles: ComponentProps<typeof ClearAllFiltersContainer> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  themeHeight: ['34px', null, null, '38px'],
};

const allFiltersWrapperStyles: ComponentProps<typeof Flex> = {
  flexWrap: 'initial',
  maxWidth: null,
  px: 'sp_8',
  py: 'sp_0',
};

export const hasSelectedFilters = (filters: Filter[]): boolean =>
  filters.length > 0 &&
  filters.some(filter => {
    if ('options' in filter) {
      return filter.options.some(option => option.selected);
    }

    return filter.selectedMax || filter.selectedMin;
  });

const CategorySelectedFilters = ({ filters }: FiltersParams) => {
  const { t } = useLocalization();

  const selectedFilters = getAllSelectedFilters(filters);

  if (!hasSelectedFilters(filters)) {
    return null;
  }

  return (
    <SelectedFiltersWrapper
      role="region"
      data-corgi-id="category-selected-filters"
      aria-roledescription={t('h24_carousel')}
      aria-label={t('h24_applied_filters')}
      {...selectedFiltersWrapperStyles}
    >
      <ClearAllFiltersContainer data-testid="clear-all-filters" {...clearAllFiltersContainerStyles}>
        <ClearAllFiltersButton filters={filters} withTextDecoration={true} />
      </ClearAllFiltersContainer>
      <Flex data-testid="all-filters-wrapper" {...allFiltersWrapperStyles}>
        {selectedFilters.map(({ url, key, range, name, text, filterKey }) => (
          <FilterChip
            key={key}
            url={url}
            range={range}
            name={name}
            text={text}
            filterKey={filterKey}
          />
        ))}
      </Flex>
    </SelectedFiltersWrapper>
  );
};

export default CategorySelectedFilters;
